/**
 * Small shared pieces: status chips (icon + words, never colour alone), the page header
 * (H1 + one-line explanation + primary action), guided empty states and filter chips.
 */

import type { ReactNode } from 'react';
import { ShellIcon } from './icons';
import type { ShellIconName } from './types';

export type StatusTone = 'ok' | 'watch' | 'open' | 'problem' | 'neutral';

const TONE_ICON: Record<StatusTone, ShellIconName> = {
  ok: 'check',
  watch: 'eye',
  open: 'info',
  problem: 'alert',
  neutral: 'info',
};

/** A status chip: tone colour plus an icon plus the words. */
export function StatusChip({ tone, children }: { tone: StatusTone; children: ReactNode }): JSX.Element {
  return (
    <span className={`ps-chip ps-chip--${tone}`}>
      <ShellIcon name={TONE_ICON[tone]} size={14} />
      <span>{children}</span>
    </span>
  );
}

export function PageHeader({
  title,
  intro,
  actions,
  titleId,
}: {
  title: ReactNode;
  intro?: ReactNode;
  /** The primary action (and at most a secondary one), right-aligned on desktop. */
  actions?: ReactNode;
  titleId?: string;
}): JSX.Element {
  return (
    <header className="ps-page-header">
      <div className="ps-page-header__text">
        <h1 className="ps-page-header__title" id={titleId}>
          {title}
        </h1>
        {intro && <p className="ps-page-header__intro">{intro}</p>}
      </div>
      {actions && <div className="ps-page-header__actions">{actions}</div>}
    </header>
  );
}

/** An empty state that says what is missing and what to do next. */
export function EmptyState({
  title,
  children,
  action,
  icon = 'info',
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ShellIconName;
}): JSX.Element {
  return (
    <div className="ps-empty" role="status">
      <span className="ps-empty__icon">
        <ShellIcon name={icon} size={22} />
      </span>
      <div className="ps-empty__text">
        <p className="ps-empty__title">{title}</p>
        {children && <div className="ps-empty__body">{children}</div>}
        {action && <div className="ps-empty__action">{action}</div>}
      </div>
    </div>
  );
}

export interface FilterChipOption {
  value: string;
  label: string;
  /** Optional count shown after the label. */
  count?: number;
}

/** A row of single-choice filter chips (a radio group drawn as chips). */
export function FilterChips({
  label,
  options,
  value,
  onChange,
}: {
  /** What the chips filter, for screen readers, e.g. "Show". */
  label: string;
  options: ReadonlyArray<FilterChipOption>;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <div className="ps-filter-chips" role="group" aria-label={label}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            className={`ps-filter-chip${on ? ' ps-filter-chip--on' : ''}`}
            aria-pressed={on}
            onClick={() => onChange(o.value)}
          >
            {on && <ShellIcon name="check" size={14} />}
            {o.label}
            {o.count !== undefined && <span className="ps-filter-chip__count">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
